import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAsyncThunkAction } from "../framework/useAsyncThunkAction";
import { fetchFormTemplates } from "../../framework/redux/slices/formTemplateSlice";
import { useAppDispatch } from "../../framework/redux/hooks";
import { addErrorMsg } from "../../framework/redux/slices/appSlice";
import { SerializedError } from "@reduxjs/toolkit";

const RETRY_COOLDOWN_MS = 5000;

export const useFetchFormTemplates = (showError = false) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [cooldown, setCooldown] = useState(false);
    const cooldownTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => clearTimeout(cooldownTimer.current);
    }, []);

    const onError = (e: SerializedError) => {
        if (e.name !== "AbortError") {
            console.error(e);
            setError(true);
            if (showError) {
                dispatch(addErrorMsg({ message: t("forms.formsLoadError") }));
            }
        }
    };

    useAsyncThunkAction(() => fetchFormTemplates(), { onError }, [retryCount]);

    const retry = useCallback(() => {
        if (cooldown) return;
        setError(false);
        setCooldown(true);
        setRetryCount((c) => c + 1);
        cooldownTimer.current = setTimeout(
            () => setCooldown(false),
            RETRY_COOLDOWN_MS
        );
    }, [cooldown]);

    return { error, retry, cooldown };
};
